/**
 * LiveTestingSxSelectModal
 *
 * sx_select 타입 질문에서 증상 옵션 클릭 시 열리는 모달
 * V1 ModalSxCollector의 V2 버전
 *
 * 기능:
 * - 증상 트리의 질문들을 렌더링
 * - 답변 입력 및 저장
 */

import { memo, useCallback, useMemo } from 'react';
import { MdClose } from 'react-icons/md';
import { BiTrash } from 'react-icons/bi';
import { intersection, isEmpty, toNumber } from 'lodash';
import { useQueries } from '@tanstack/react-query';
import { getQuestionControllerFindByIdQueryOptions } from '../../../../api/generated/questions/questions';
import type { QuestionResponseDto, QuestionDetailResponseDto, ShowConditionDto } from '../../../../api/generated/types';
import { useStaticLocales, type SystemLocaleCodes } from '../../../../../admin/hooks/locales/use-static-locale';
import LiveTestingQuestionFieldInner from './LiveTestingQuestionFieldInner';
import LiveTestingContainer from './LiveTestingContainer';
import { getLocaleCode } from '../../../organization/configurations/language/constants';
import type { LiveTestingAnswerValue } from './types';
import { normalizeQuestionLabelForSurveyLocale } from '../../../../shared/utils/survey-locale-text';

interface LiveTestingSxSelectModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean;
  /** 증상 이름 (모달 제목) */
  symptomName: string;
  /** 증상 트리의 질문들 */
  questions: QuestionResponseDto[];
  /** 모달 내 답변 상태 */
  answers: Record<string, LiveTestingAnswerValue>;
  /** 답변 업데이트 핸들러 */
  onAnswerChange: (questionKey: string, value: LiveTestingAnswerValue) => void;
  /** 저장 핸들러 */
  onSave: () => void;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 삭제 핸들러 (선택적) */
  onDelete?: () => void;
  /** 로딩 중 */
  isLoading?: boolean;
  /** 에러 메시지 */
  error?: string | null;
  /** 저장 버튼 텍스트 */
  saveButtonText?: string;
  /** 삭제 버튼 텍스트 */
  deleteButtonText?: string;
}

function LiveTestingSxSelectModal({
  isOpen,
  symptomName,
  questions,
  answers,
  onAnswerChange,
  onSave,
  onClose,
  onDelete,
  isLoading = false,
  error = null,
  saveButtonText,
  deleteButtonText,
}: LiveTestingSxSelectModalProps) {
  // Container에서 isMale/currentLocale 가져오기 (body selector 이미지용 + placeholder 번역)
  const { isMale, currentLocale } = LiveTestingContainer.useContainer();

  const localeCode = getLocaleCode(currentLocale || 'english') as SystemLocaleCodes;
  const { sltStr } = useStaticLocales({ localeCode });
  const resolvedSaveButtonText = saveButtonText ?? sltStr({ key: 'btn-save', capit: true });
  const resolvedDeleteButtonText =
    deleteButtonText ?? sltStr({ key: 'btn-delete-symptom', title: true });
  const extractAnswerKeys = useCallback((value?: LiveTestingAnswerValue): string[] => {
    if (value == null) return [];
    if (typeof value === 'string') return [value];
    if (typeof value === 'number') return [String(value)];
    if (Array.isArray(value)) return value.map((item) => String(item));
    if (typeof value === 'object' && 'selectedKeys' in value) {
      return (value.selectedKeys as string[]).map((item) => String(item));
    }
    if (typeof value === 'object' && 'answerKeys' in (value as Record<string, unknown>)) {
      return ((value as { answerKeys?: string[] }).answerKeys || []).map((item) => String(item));
    }
    if (typeof value === 'object' && 'answerValues' in (value as Record<string, unknown>)) {
      return ((value as { answerValues?: Array<string | number> }).answerValues || []).map((item) => String(item));
    }
    return [];
  }, []);

  const extractNumericValue = useCallback((value?: LiveTestingAnswerValue): number | null => {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = toNumber(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (Array.isArray(value) && value.length > 0) {
      const parsed = toNumber(value[0]);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'object' && 'selectedKeys' in value) {
      const firstKey = (value.selectedKeys as string[])[0];
      const parsed = toNumber(firstKey);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'object' && 'answerValues' in (value as Record<string, unknown>)) {
      const firstValue = (value as { answerValues?: Array<string | number> }).answerValues?.[0];
      const parsed = toNumber(firstValue);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }, []);

  const questionDetailQueries = useQueries({
    queries: questions.map((question) => {
      const queryOptions = getQuestionControllerFindByIdQueryOptions(question.id);
      return {
        ...queryOptions,
        enabled: Boolean(question.id),
      };
    }),
  });

  const questionDetailsMap = useMemo(() => {
    const map = new Map<number, QuestionDetailResponseDto>();
    questions.forEach((question, index) => {
      const detail = questionDetailQueries[index]?.data as QuestionDetailResponseDto | undefined;
      if (detail) {
        map.set(question.id, detail);
      }
    });
    return map;
  }, [questionDetailQueries, questions]);

  const visibleQuestions = useMemo(() => {
    return questions.filter((question, index) => {
      if (index === 0) {
        return true;
      }
      const detail = questionDetailsMap.get(question.id);
      const showConditions =
        ((question as any).showConditions as ShowConditionDto[] | undefined | null) ||
        (detail?.showConditions as ShowConditionDto[] | undefined | null) ||
        null;

      if (!showConditions || showConditions.length === 0) {
        if (question.hasConditions) {
          return false;
        }
        return true;
      }

      const evaluateCondition = (condition: ShowConditionDto) => {
        const { operator, qsKey, value, keys, type, uiMetadata } = condition;
        const answerValue = qsKey ? answers[qsKey] : undefined;

        if (uiMetadata && answerValue && typeof answerValue === 'object' && 'uiMetadata' in answerValue) {
          const targetMeta = uiMetadata as Record<string, string>;
          const answerMeta = (answerValue as { uiMetadata?: Record<string, string> }).uiMetadata || {};
          const allMatch = Object.keys(targetMeta).every((key) => targetMeta[key] === answerMeta[key]);
          if (allMatch) {
            return true;
          }
        }

        if (operator) {
          const lhs = extractNumericValue(answerValue);
          const rhs = toNumber(value);
          if (lhs !== null && Number.isFinite(lhs) && Number.isFinite(rhs)) {
            if (operator === 'lte' && lhs <= rhs) return true;
            if (operator === 'lt' && lhs < rhs) return true;
            if (operator === 'gte' && lhs >= rhs) return true;
            if (operator === 'gt' && lhs > rhs) return true;
            if (operator === 'eq' && lhs === rhs) return true;
            if (operator === 'ne' && lhs !== rhs) return true;
            return false;
          }

          const rhsStr = value ?? '';
          const answerKeys = extractAnswerKeys(answerValue);
          if (operator === 'eq') {
            return answerKeys.some((item) => item === rhsStr);
          }
          if (operator === 'ne') {
            return answerKeys.every((item) => item !== rhsStr);
          }
        }

        if (type === 'include' || (keys && keys.length > 0)) {
          const answerKeys = extractAnswerKeys(answerValue);
          return !isEmpty(intersection(answerKeys, keys || []));
        }

        return false;
      };

      const andConditions = showConditions.filter((cond) => cond.logicType === 'AND');
      const orConditions = showConditions.filter((cond) => cond.logicType === 'OR' || !cond.logicType);

      const andMet = andConditions.length === 0 || andConditions.every((condition) => evaluateCondition(condition));
      const orMet = orConditions.length === 0 || orConditions.some((condition) => evaluateCondition(condition));

      return andMet && orMet;
    });
  }, [answers, extractAnswerKeys, extractNumericValue, questions, questionDetailsMap]);

  // Content renderer to avoid nested ternaries
  const renderContent = useCallback(() => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-20 text-red-500">
          {error}
        </div>
      );
    }

    if (visibleQuestions.length === 0) {
      return (
        <div className="flex items-center justify-center py-20 text-gray-500">
          {sltStr({ key: 'modal-question-add-no-results' })}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        {visibleQuestions.map((question) => (
          <div key={question.questionKey} className="flex flex-col gap-2">
            {/* Question Label */}
            <label className="text-sm font-medium text-[#222638]">
              {normalizeQuestionLabelForSurveyLocale({
                locale: currentLocale,
                label: question.label,
              }).replaceAll('{{symptom}}', symptomName)}
              {question.optional === true && (
                <span className="text-[#737580] font-normal ml-1">
                  ({sltStr({ key: 'optional' })})
                </span>
              )}
            </label>

            {/* Question Field */}
            <LiveTestingQuestionFieldInner
              question={question}
              value={answers[question.questionKey]}
              onChange={(value) => onAnswerChange(question.questionKey, value)}
              isMale={isMale}
              locale={currentLocale}
            />
          </div>
        ))}
      </div>
    );
  }, [isLoading, error, visibleQuestions, answers, onAnswerChange, symptomName, isMale, currentLocale, sltStr]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-lg bg-white rounded-t-xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Handle */}
        <div className="flex justify-center pt-3">
          <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <h3 className="text-lg font-semibold text-[#222638] flex-1 text-center">
            {symptomName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors absolute right-2"
          >
            <MdClose size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="flex-none px-5 py-4 border-t border-gray-100 flex flex-col gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {resolvedSaveButtonText}
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={isLoading}
              className="btn btn-outline btn-error w-full"
            >
              <BiTrash size={18} />
              {resolvedDeleteButtonText}
            </button>
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default memo(LiveTestingSxSelectModal);
